import { useCallback } from 'react'
import type { Payable } from '@klear/payable/payable'
import { trpc, type RouterInputs } from '@klear/ui/lib/trpc'

export type PayableListFilters = RouterInputs['payable']['list']

type PayableCommitInput = RouterInputs['payable']['commit']
type PayableCreateInput = Extract<PayableCommitInput, { id?: undefined }>
type PayableUpdateInput = Extract<PayableCommitInput, { id: number }>

const EMPTY_PAYABLES: Payable[] = []

export function usePayables(
  traderId: number,
  filters?: Omit<PayableListFilters, 'traderId'>,
  options?: { enabled?: boolean }
) {
  const { data, error, isLoading, refetch } = trpc.payable.list.useQuery(
    { ...(filters ?? {}), traderId },
    {
      enabled: traderId > 0 && options?.enabled !== false,
      refetchInterval: (query) => (query.state.data?.payables.some((payable) => payable.isSending) ? 5000 : false)
    }
  )

  const payables = data?.payables ?? EMPTY_PAYABLES

  return {
    payables,
    total: data?.total ?? 0,
    error,
    isLoading,
    refetch
  }
}

export function useOperationalPayments(operationalEventId: number | undefined, traderId: number) {
  const { data, error, isLoading, refetch } = trpc.payable.paymentsForOperationalEvent.useQuery(
    { operationalEventId: operationalEventId ?? 0, traderId },
    { enabled: operationalEventId != null && operationalEventId > 0 && traderId > 0 }
  )

  const payments = data ?? EMPTY_PAYABLES

  return { payments, error, isLoading, refetch }
}

export function usePayable(id: number, options: { enabled?: boolean; traderId: number }) {
  const {
    data: payable,
    error,
    isLoading
  } = trpc.payable.get.useQuery(
    { id, traderId: options.traderId },
    {
      enabled: !!id && options.enabled !== false
    }
  )

  return { payable: payable ?? undefined, error, isLoading }
}

export function usePayableMutations() {
  const previewMutation = trpc.payable.preview.useMutation()
  const commitMutation = trpc.payable.commit.useMutation()
  const markPaidMutation = trpc.payable.markPaid.useMutation()

  const previewNewPayable = useCallback(
    (input: PayableCreateInput) =>
      previewMutation.mutateAsync({
        traderId: input.traderId,
        data: input.data
      } satisfies RouterInputs['payable']['commit']),
    [previewMutation.mutateAsync]
  )

  const previewPayable = useCallback(
    (input: PayableUpdateInput) =>
      previewMutation.mutateAsync({
        id: input.id,
        traderId: input.traderId,
        data: input.data
      } satisfies RouterInputs['payable']['commit']),
    [previewMutation.mutateAsync]
  )

  const commitNewPayable = useCallback(
    (input: PayableCreateInput) =>
      commitMutation.mutateAsync({
        traderId: input.traderId,
        data: input.data
      } satisfies RouterInputs['payable']['commit']),
    [commitMutation.mutateAsync]
  )

  const commitPayable = useCallback(
    (input: PayableUpdateInput) =>
      commitMutation.mutateAsync({
        id: input.id,
        traderId: input.traderId,
        data: input.data
      } satisfies RouterInputs['payable']['commit']),
    [commitMutation.mutateAsync]
  )

  const previewPayableCommit = useCallback(
    (input: RouterInputs['payable']['commit']) => previewMutation.mutateAsync(input),
    [previewMutation.mutateAsync]
  )

  const commitPayableCommit = useCallback(
    (input: RouterInputs['payable']['commit']) => commitMutation.mutateAsync(input),
    [commitMutation.mutateAsync]
  )

  const markPaid = useCallback(
    (input: RouterInputs['payable']['markPaid']) => markPaidMutation.mutateAsync(input),
    [markPaidMutation.mutateAsync]
  )

  return {
    previewNewPayable,
    previewPayable,
    commitNewPayable,
    commitPayable,
    previewPayableCommit,
    commitPayableCommit,
    markPaid,
    isPreviewing: previewMutation.isPending,
    isCommitting: commitMutation.isPending || markPaidMutation.isPending
  }
}
